import { useEffect, useState } from "react";
import { ref, getDownloadURL } from "firebase/storage";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  limit,
  increment,
  setDoc,
} from "firebase/firestore";
import {
  LlmContent,
  ProfileNutshell,
  PublicProfile,
  SpectrumData,
  SummaryTable as SummaryTableData,
  TwoByTwoGridNew,
} from "@dimensional-engineering/dimensional-models";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

import styles from "@/styles/profiles/PublicProfile.module.css";

import { db, firebaseStorage } from "@/_firebase/firebaseConfig";
import CTAModal from "@/components/shared/CTAModal/CTAModal";
import useScrollBlock from "../helpers/useScroll";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import Footers from "@/components/shared/Footers/Footers";
import { Mixpanel } from "@/helpers/mixpanel";
import { callFirebaseFunctions } from "@/_firebase/callFirebaseFunctions";
import {
  FriendsPublicProfile,
  HighestScoringTraitsModel,
} from "@/models/sharedModels";
import { getDefaultProfilePhoto } from "@/context/utils";
import { seoUidList } from "@/assets/seoUidList";
import PublicProfileHeader from "@/components/publicProfiles/PublicProfileHeader/PublicProfileHeader";
import PublicProfileTabs from "@/components/publicProfiles/PublicProfileTabs/PublicProfileTabs";
import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";
import SummaryTable from "@/components/publicProfiles/SummaryTable/SummaryTable";
import { ProfileColorMap } from "@/components/shared/ColorMap";
import PublicProfilesNatureBreakdown, {
  PublicProfilesInfoBubble,
} from "@/components/publicProfiles/PublicProfilesNatureBreakdown/PublicProfilesNatureBreakdown";
import NewProfileNutshell from "@/components/publicProfiles/Nutshell/NewProfileNutshell";
import { HighestScoringTraitsPublicProfiles } from "@/components/publicProfiles/HighestScoringTraits/HighestScoringTraits";
import { SEOSPectrums } from "@/components/shared/Spectrum/Spectrums";
import PublicProfilesGhostBox from "@/components/publicProfiles/PublicProfilesGhostBox/PublicProfilesGhostBox";
import TwoByTwoNew from "@/components/shared/TwoByTwoNew/TwoByTwoNew";
import ConnectionsModal from "@/components/publicProfiles/ConnectionsModal/ConnectionsModal";

export type SEOProfilePageProps = {
  publicProfile: PublicProfile;
  slug: string;
  uid: string;
  imageUrl: string | null;
  summaryTableData: SummaryTableData | null;
  nutshellData: LlmContent[] | string | null | undefined;
  connectionList: FriendsPublicProfile[] | [];
  cognitionSpectrumData: SpectrumData[] | null;
  twoByTwoData: TwoByTwoGridNew | null;
};

export default function PublicProfilePage(props: SEOProfilePageProps) {
  const [showConnections, setShowConnections] = useState<boolean>(false);

  const [blockScroll, allowScroll] = useScrollBlock();
  const { modal, setModal } = usePublicProfileContext();
  const connectionListWihtImage = useGetConnectionListImages({
    connectionList: props.connectionList,
    uid: props.uid,
  });
  const highestScoringTraits = useGetHighestScoringTraits(
    props.uid,
    props.publicProfile.publicProfilePermissions?.endorsedElements
  );

  const colorMap = ProfileColorMap;
  const natureColor = props.publicProfile.primaryNature?.nature
    ? colorMap[props.publicProfile.primaryNature?.nature]
    : "";

  useEffect(() => {
    if (showConnections === true) {
      blockScroll();
    } else {
      allowScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConnections]);

  useEffect(() => {
    if (props.publicProfile.ownerUUID) {
      Mixpanel?.track(
        "Unauthenticated profile viewed",
        {
          username: props.publicProfile.userName,
        },
        "d"
      );
    }

    const ref = doc(
      db,
      `publicProfileViewings/${props.publicProfile.ownerUUID}`
    );

    setDoc(
      ref,
      {
        cumulativeWeeklyCount: increment(1),
        memberId: props.publicProfile.ownerUUID,
      },
      { merge: true }
    )
      .then((res) => {
        console.log("Successful increment");
      })
      .catch((err) => {
        console.log("Error incrementing", err);
      });

    document.body.style.overflowX = "visible";
    return () => {
      document.body.style.overflowX = "hidden";
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.publicProfile.ownerUUID]);

  return (
    <div
      onClick={(e) => {
        modal ? setModal(false) : null;
      }}
      className={styles.main}
    >
      <Head>
        <title>{`${
          props.publicProfile.name ?? props.publicProfile.userName
        } | Dimensional`}</title>
        <meta
          name="description"
          content={props.publicProfile.bio ?? props.publicProfile.name}
        />
        <meta
          property="og:description"
          content={props.publicProfile.bio ?? props.publicProfile.name}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          property="og:image"
          content={props.imageUrl === null ? undefined : props.imageUrl}
        />
        <link rel="icon" href="/favicon.png" />
      </Head>
      <main className={styles.main}>
        <PublicProfileHeader
          publicProfile={props.publicProfile}
          imageUrl={props.imageUrl ?? ""}
          onOpenConnectionList={() => setShowConnections(true)}
        />
        <PublicProfileTabs
          tabs={[
            "summary",
            "nature",
            "description",
            "traits",
            "cognition",
            "disc",
          ]}
          publicProfile={props.publicProfile}
          imageUrl={props.imageUrl ?? ""}
        />
        <section
          style={{ padding: "0", marginTop: "0" }}
          className={styles.section}
          id="summary"
        >
          {props.publicProfile.bio && (
            <div className={styles.bioDiv}>
              <SmallProfileImage
                imageUrl={props.imageUrl ?? ""}
                publicProfile={props.publicProfile}
                noLink
              />
              <p className={styles.bio}>{props.publicProfile.bio}</p>
            </div>
          )}
          <div className={styles.summaryTableDiv}>
            <SummaryTable
              natureColor={natureColor}
              data={props.summaryTableData}
              publicProfile={props.publicProfile}
              imageUrl={props.imageUrl ?? ""}
            />
          </div>
        </section>
        <section className={styles.section} id="nature">
          <h2 className={styles.header}>Nature</h2>
          <PublicProfilesNatureBreakdown publicProfile={props.publicProfile} />
        </section>
        <section className={styles.section} id="description">
          <h2 className={styles.header}>Description</h2>
          <div className={styles.nutshellDiv}>
            {props.nutshellData ? (
              <NewProfileNutshell nutshellData={props.nutshellData} />
            ) : (
              <div>
                <PublicProfilesGhostBox />
              </div>
            )}
          </div>
        </section>
        <section
          style={{ padding: "0" }}
          className={styles.section}
          id="traits"
        >
          <h2 className={styles.headerTraits}>Highest Scoring Traits</h2>
          <div className={styles.infoBubbleDIvrTraits}>
            <PublicProfilesInfoBubble title="Dimensional measures 150 traits that define our personality, values, and attitudes. They are measured through self and peer assessments." />
          </div>
          <div className={styles.nutshellDiv}>
            <HighestScoringTraitsPublicProfiles
              data={highestScoringTraits}
              publicProfile={props.publicProfile}
            />
          </div>
        </section>
        <section className={styles.section} id="cognition">
          <h2 className={styles.header}>Cognition</h2>
          <div className={styles.infoBubbleDIv}>
            <PublicProfilesInfoBubble title="Based on the MBTI model, Cognition measures how we orient ourselves to the world, process reality, and make decisions." />
          </div>
          {props.cognitionSpectrumData ? (
            <SEOSPectrums
              otherPublicProfile={props.publicProfile}
              imageUrl={props.imageUrl ?? ""}
              cachedData={props.cognitionSpectrumData ?? undefined}
            />
          ) : (
            <div style={{ marginTop: "30px" }}>
              <PublicProfilesGhostBox />
            </div>
          )}
        </section>

        <section className={styles.section} id="disc">
          <h2 className={styles.header}>Interaction</h2>
          <div className={styles.infoBubbleDIv}>
            <PublicProfilesInfoBubble title="Based on the DISC model of personality, Interaction styles are how we relate to and communicate with others." />
          </div>
          {props.twoByTwoData ? (
            <div className={styles.twoByTwoDiv}>
              <TwoByTwoNew
                data={props.twoByTwoData}
                imageUrl={props.imageUrl ?? ""}
                publicProfile={props.publicProfile}
              />
            </div>
          ) : (
            <div style={{ marginTop: "30px" }}>
              <PublicProfilesGhostBox />
            </div>
          )}
        </section>

        <Footers
          connectionList={
            props.publicProfile?.publicProfilePermissions?.connectionList ===
            true
              ? props.connectionList
              : undefined
          }
        />
      </main>
      {modal === true && (
        <CTAModal
          HeadingText="Get Dimensional"
          userName={props.publicProfile.userName ?? null}
          uid={props.publicProfile.ownerUUID ?? null}
        />
      )}
      {showConnections === true && (
        <ConnectionsModal
          connectionList={connectionListWihtImage}
          closeModal={(e) => {
            setShowConnections(false);
          }}
          permission={
            props.publicProfile.publicProfilePermissions?.connectionList
              ? true
              : false
          }
          userName={props.publicProfile.userName!!}
        />
      )}
    </div>
  );
}

function useGetConnectionListImages(props: {
  connectionList: FriendsPublicProfile[];
  uid: string;
}) {
  const [connectionListWihtImage, setConnectionListWithImage] = useState<
    FriendsPublicProfile[]
  >([]);

  async function getConnectionImage(uid: string) {
    let imageUrl = "";
    await getDownloadURL(
      ref(
        firebaseStorage,
        `gs://${process.env.NEXT_PUBLIC_STORAGEBUCKET}/profileImages/${uid}/thumbnail.jpg`
      )
    )
      .then((url) => {
        imageUrl = url;
      })
      .catch(async () => {
        await getDefaultProfilePhoto(uid).then((value) => {
          imageUrl = value;
        });
      });

    return imageUrl;
  }

  useEffect(() => {
    setConnectionListWithImage([]);
    props.connectionList.forEach((person) => {
      getConnectionImage(person.ownerUUID).then((url) => {
        let p = person;
        p.imageUrl = url;
        setConnectionListWithImage((current) => [...current, p]);
      });
    });
  }, [props.connectionList, props.uid]);

  return connectionListWihtImage;
}

function useGetHighestScoringTraits(
  uid: string,
  permission: boolean | undefined
) {
  const [highestScoringTraits, sethighestScoringTraits] =
    useState<HighestScoringTraitsModel | null>(null);

  useEffect(() => {
    sethighestScoringTraits(null);
    if (permission == true) {
      const getHighestScores = callFirebaseFunctions(
        "fetchProfileHighestScoringTraits"
      );

      getHighestScores({ uid: uid })
        .then((res) => {
          if (res.data) {
            sethighestScoringTraits(res.data as HighestScoringTraitsModel);
          }
        })
        .catch((err) => {
          sethighestScoringTraits(null);
        });
    } else {
      sethighestScoringTraits(null);
    }
  }, [permission, uid]);

  return highestScoringTraits;
}

export const getStaticPaths: GetStaticPaths = async () => {
  const buildProfiles = process.env.NEXT_PUBLIC_BUILD_PUBLIC_PROFILES;
  console.log(buildProfiles);
  if (buildProfiles === "false") {
    console.log("not building");
    return { paths: [], fallback: "blocking" };
  }
  const list: string[] = seoUidList.slice(170000, 180000);

  const paths = list.map((username) => ({
    params: { slug: username },
  }));

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  let slug;
  let userId: string | null = null;
  let publicProfileData: PublicProfile | null = null;
  let imageUrl = null;
  let summaryTableData = null;
  let nutshellData: LlmContent[] | string | null | undefined = null;
  let connectionList: FriendsPublicProfile[] = [];
  let cognitionSpectrumData: SpectrumData[] | null = null;
  let twoByTwoData: TwoByTwoGridNew | null = null;

  async function handleConnections(userUid: string) {
    const reference = doc(db, `members/${userUid}/accessMaps/friends`);
    const document = await getDoc(reference);
    if (document.exists()) {
      let list: string[] = Object.keys(document.data().accessMap);
      return await Promise.all(
        list.map(async (id) => {
          let profileRef = doc(db, "publicProfiles", id);
          await getDoc(profileRef).then((res) => {
            if (res.exists()) {
              let docData = res.data() as FriendsPublicProfile;
              connectionList.push(docData);
            }
          });
        })
      );
    }
  }

  if (ctx?.params?.slug) {
    slug = ctx.params.slug;

    const publicProfileRef = collection(db, "publicProfiles");

    if (slug) {
      const allPromises: Promise<any>[] = [];

      const q = query(
        publicProfileRef,
        where("userNameLowerCase", "==", slug.toString().toLowerCase()),
        limit(1)
      );

      const docsArray: PublicProfile[] = [];
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        docsArray.push(doc.data() as PublicProfile);
      });
      publicProfileData = docsArray[0];

      if (docsArray.length > 0) {
        userId = docsArray[0].ownerUUID;
        const permissions = docsArray[0].publicProfilePermissions;

        const getSummaryTable = callFirebaseFunctions("fetchSummaryTable");
        allPromises.push(
          getSummaryTable({ uid: userId })
            .then((result) => {
              summaryTableData = result.data;
            })
            .catch((err) => {
              summaryTableData = null;
            })
        );

        if (permissions?.adjectives) {
          const getProfileNutshell = callFirebaseFunctions(
            "fetchProfileNutshell"
          );
          allPromises.push(
            getProfileNutshell({ uid: userId })
              .then((res) => {
                const nutshellDataResult = res.data as
                  | ProfileNutshell
                  | null
                  | undefined;
                if (nutshellDataResult?.llmContent) {
                  nutshellData = nutshellDataResult?.llmContent;
                } else if (nutshellDataResult?.body) {
                  nutshellData = nutshellDataResult?.body;
                } else {
                  nutshellData = null;
                }
              })
              .catch((err) => {
                nutshellData = null;
              })
          );
        }

        if (permissions?.archetypes) {
          const spectrumCall = callFirebaseFunctions("fetchSpectrumData");

          allPromises.push(
            spectrumCall({
              contentIdentifier: {
                contentType: "publicProfile",
                contentSlug: "personality-II",
              },
              peerUid: userId,
            })
              .then((result) => {
                cognitionSpectrumData = result.data as SpectrumData[];
              })
              .catch((err) => {
                console.log("error", err);
              })
          );

          const twoByTwoCall = callFirebaseFunctions("fetchTwoByTwoGrid");

          allPromises.push(
            twoByTwoCall({
              memberId: userId,
              slug: "interaction-style-archetype-agnostic",
            })
              .then((res) => {
                if (res.data) {
                  twoByTwoData = res.data as TwoByTwoGridNew;
                }
              })
              .catch((err) => {
                console.log(err);
              })
          );
        }

        if (permissions?.connectionList) {
          allPromises.push(handleConnections(userId));
        }

        allPromises.push(
          getDownloadURL(
            ref(
              firebaseStorage,
              `gs://${process.env.NEXT_PUBLIC_STORAGEBUCKET}/profileImages/${docsArray[0].ownerUUID}/cropped.jpg`
            )
          )
            .then((url) => {
              imageUrl = url;
            })
            .catch(async (err) => {
              await getDefaultProfilePhoto(docsArray[0].ownerUUID).then(
                (url) => {
                  imageUrl = url;
                }
              );
            })
        );

        await Promise.all(allPromises);
      }
    }
  }

  if (!publicProfileData) {
    return {
      notFound: true,
      revalidate: 10,
    };
  }

  return {
    props: {
      slug: slug ? slug : null,
      publicProfile: publicProfileData ? publicProfileData : null,
      uid: userId ? userId : null,
      imageUrl: imageUrl,
      summaryTableData: summaryTableData,
      nutshellData: nutshellData,
      connectionList: connectionList,
      cognitionSpectrumData: cognitionSpectrumData,
      twoByTwoData: twoByTwoData,
    },
    // revalidate: 86400,
    revalidate: 10,
  };
};
